import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trophy, Headphones, Eye, Users, Gift, ArrowRight } from "lucide-react";
import { api } from "../lib/api";

/** Section page d'accueil : concept Artiste du Mois + sponsor + Top 5 + CTA. */
export default function AomHomeSection() {
    const [data, setData] = useState(null);
    useEffect(() => {
        api.get("/aom/current").then(({ data }) => setData(data)).catch(() => {});
    }, []);

    const ed = data?.edition;
    if (!ed) return null;
    const top5 = (data.top || []).slice(0, 5);
    const sponsor = ed.sponsor;

    return (
        <section className="border-t border-[#1c1c1c] bg-[#0a0a08]" data-testid="aom-home-section">
            <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-start">
                    {/* Concept + sponsor + CTA */}
                    <div>
                        <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#D4AF37] flex items-center gap-2">
                            <Trophy className="w-4 h-4" /> Concours officiel — {data.month_label}
                        </p>
                        <h2 className="mt-3 font-display text-3xl md:text-4xl font-semibold tracking-tight">
                            MBOAZICK <span className="text-[#D4AF37]">Artiste du Mois</span>
                        </h2>
                        <p className="mt-4 text-[#C8C8C8] leading-relaxed text-sm md:text-base">
                            Chaque mois, l&apos;artiste le plus actif et engagé de la plateforme est désigné automatiquement
                            par le <strong className="text-[#F4F4F4]">Score MBOAZICK</strong> : écoutes, visites de profil,
                            nouveaux abonnés, publications et cadeaux reçus. Pas un concours de popularité —
                            un label de professionnalisme, avec <strong className="text-[#D4AF37]">{(ed.prize_fcfa || 25000).toLocaleString("fr-FR")} FCFA</strong> et
                            de nombreux avantages à la clé.
                        </p>

                        {sponsor && (
                            <div className="mt-6 flex items-center gap-4 bg-[#101010] border border-[#D4AF37]/25 rounded-2xl p-4" data-testid="aom-home-sponsor">
                                {sponsor.logo_url && <img decoding="async" loading="lazy" src={sponsor.logo_url} alt={sponsor.name} className="w-14 h-14 rounded-lg object-contain bg-white/95 p-1.5 shrink-0" />}
                                <div className="min-w-0">
                                    <p className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] font-bold">Sponsor du mois</p>
                                    <p className="font-semibold text-sm truncate">{sponsor.name}</p>
                                    {sponsor.text && <p className="text-xs text-[#A8A8A8] line-clamp-1">{sponsor.text}</p>}
                                </div>
                            </div>
                        )}

                        <Link
                            to="/artiste-du-mois"
                            className="mt-7 inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-[#e6c14d] text-[#0F0F0F] font-bold px-7 py-3.5 rounded-full transition-colors"
                            data-testid="aom-home-cta"
                        >
                            Découvrir le concours <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>

                    {/* Top 5 en direct */}
                    <div data-testid="aom-home-top5">
                        <div className="flex items-center justify-between">
                            <p className="font-display text-lg font-semibold">Top 5 provisoire</p>
                            <span className="text-[10px] font-bold uppercase tracking-widest text-[#00d97e] bg-[#00d97e]/10 px-3 py-1 rounded-full">En direct</span>
                        </div>
                        {top5.length === 0 ? (
                            <p className="mt-5 text-sm text-[#A8A8A8]">Aucun point marqué pour l&apos;instant — les artistes actifs ce mois-ci apparaîtront ici.</p>
                        ) : (
                            <div className="mt-4 space-y-2">
                                {top5.map((s, i) => (
                                    <div key={s.artist_id} className={`flex items-center gap-3 p-3 rounded-xl border ${i === 0 ? "border-[#D4AF37]/50 bg-[#15120a]" : "border-[#262626] bg-[#101010]"}`} data-testid={`aom-home-rank-${i + 1}`}>
                                        <span className={`font-mono font-bold w-7 text-center shrink-0 ${i === 0 ? "text-[#D4AF37]" : "text-[#6E6E6E]"}`}>{i === 0 ? "🏆" : `#${i + 1}`}</span>
                                        {s.avatar_url ? <img decoding="async" loading="lazy" src={s.avatar_url} alt="" className="w-10 h-10 rounded-full object-cover shrink-0" /> : <div className="w-10 h-10 rounded-full bg-[#262626] shrink-0" />}
                                        <div className="flex-1 min-w-0">
                                            {s.username ? (
                                                <Link to={`/artistes/${s.username}`} className="font-medium text-sm truncate block hover:text-[#00d97e]">{s.name}</Link>
                                            ) : <p className="font-medium text-sm truncate">{s.name}</p>}
                                            <div className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-[#6E6E6E]">
                                                <span className="inline-flex items-center gap-1"><Headphones className="w-3 h-3 text-[#00c0ff]" /> {s.metrics.plays} écoutes</span>
                                                <span className="inline-flex items-center gap-1"><Eye className="w-3 h-3 text-[#c084fc]" /> {s.metrics.visits} visites</span>
                                                <span className="inline-flex items-center gap-1"><Users className="w-3 h-3 text-[#ffc83d]" /> {s.metrics.new_followers} abonnés</span>
                                                <span className="inline-flex items-center gap-1"><Gift className="w-3 h-3 text-[#ff6b35]" /> {s.metrics.gifts} cadeaux</span>
                                            </div>
                                        </div>
                                        <span className="font-mono font-bold text-sm text-[#00d97e] shrink-0">{s.score} pts</span>
                                    </div>
                                ))}
                            </div>
                        )}
                        <p className="mt-3 text-[11px] text-[#6E6E6E]">Score = activité (publications) + engagement (écoutes, abonnés, cadeaux) + visites de profil + participation Talent.</p>
                    </div>
                </div>
            </div>
        </section>
    );
}
